import React from "react";
import branchServerClient from "web/core/branchServerClient";
import Link from "theme/components/atoms/Typography/Link";
import { useStorefrontContentMetadata } from "theme/modules/StorefrontContent";
import withIsFeatureActive from "web/core/UNSAFE_featureFlags/withIsFeatureActive";
import storefrontConfig from "./StorefrontContentConfig";
import logHandler from "web/core/logs/logHandler";

const warnForUnknownSource = (source) => {
  const message = `Content metadata contains data for a source that was not configured: "${source}". Please update your StorefrontContentConfig.js accordingly.`;
  logHandler.getLogger().warn({ message });
  if (process.env.NODE_ENV === "development") {
    console.warn(message);
  }
};

const ContentLink = ({ contentMetadata, sourceConfig }) => {
  return (
    <div
      className="storefront-content__edit-link"
      style={{
        "--source-brand-color": sourceConfig.color,
      }}
    >
      <Link to={contentMetadata.editUrl}>
        {sourceConfig.icon && (
          <span className="storefront-content__edit-link-icon">
            {sourceConfig.icon}
          </span>
        )}
        <span className="storefront-content__edit-link-title">
          {contentMetadata.type} (Id #{contentMetadata.id})
        </span>
      </Link>
    </div>
  );
};

const StorefrontContent = ({
  type,
  id,
  children,
  scope = "block",
  xRayMode,
}) => {
  /** @type {import("./ContentMetadataStore").ContentMetadata[]} */
  const contentMetadataList = useStorefrontContentMetadata(type, id);
  if (!xRayMode.isFeatureActive || !contentMetadataList.length) {
    return <>{children}</>;
  }

  const mainSource = contentMetadataList[0].source;
  const mainSourceConfig = storefrontConfig.sources.find(
    ({ name }) => name === mainSource
  );

  if (!mainSourceConfig) {
    warnForUnknownSource(mainSource);
    return <>{children}</>;
  }

  const links = contentMetadataList.map((contentMetadata, index) => {
    const sourceConfig = storefrontConfig.sources.find(
      ({ name }) => name === contentMetadata.source
    );
    if (!sourceConfig) {
      warnForUnknownSource(contentMetadata.source);
      return null;
    }
    return (
      <ContentLink
        key={contentMetadata.hash}
        contentMetadata={contentMetadata}
        sourceConfig={sourceConfig}
      />
    );
  });

  return (
    <div
      className={`storefront-content storefront-content--${mainSource} storefront-content--${scope}`}
      style={{ "--source-brand-color": mainSourceConfig.color }}
    >
      <div className="storefront-content__tools">{links}</div>
      <div className="storefront-content__initial-content">{children}</div>
    </div>
  );
};

export default branchServerClient(
  () => {
    return ({ children }) => {
      return <>{children}</>;
    };
  },
  (component) => withIsFeatureActive("xRayMode", "xRayMode")(StorefrontContent)
)(StorefrontContent);
